/* eslint-disable formatjs/no-literal-string-in-jsx */
/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button } from '../../components/Button/Button'
import { Section } from '../../components/Section'
import { TextLayout } from '../../components/TextLayout'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import { SMALL_BREAKPOINT } from '../../utils/breakpoints'
import { getPricingPlans, type PricingPlan } from '../pricing/data'

function TeaserPlanItem({ plan }: { plan: PricingPlan }) {
  const _intl = useIntl()
  const yearlyPrice = plan.price?.yearly

  return (
    <div
      className={classNames(
        'grid-span-4-large grid-span-6-medium grid-span-12-small',
        'overflow-hidden',
        'position-relative'
      )}
      css={css`
        margin-top: ${plan.accentuated ? '-32px' : '0'};
        /* On mobile the cards stack vertically, so the -32px lift on the
           accentuated card would push its 'Recommended' label over the card
           above it. Reset the lift on small screens — vertical stack already
           gives the label plenty of room. */
        @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
          margin-top: 0;
        }
      `}
      style={{
        alignSelf: 'start',
        backgroundColor: 'var(--saturn-orange)',
        padding: plan.accentuated ? '32px 2px 2px 2px' : 0,
        borderRadius: plan.accentuated ? 18 : 16,
      }}
    >
      {plan.accentuated && (
        <div
          className="position-absolute flexbox flexbox-centered"
          style={{ top: 0, left: 0, right: 0, height: 32 }}
        >
          <div className="uppercase font-size-8 font-weight-bold white">
            <FormattedMessage
              id="home.pricing.recommended"
              defaultMessage="Recommended"
            />
          </div>
        </div>
      )}
      <div
        className={classNames(
          'position-relative overflow-hidden',
          'background-shade-100',
          'border-small border-alpha-shade-40',
          'padding-horizontal-16 padding-top-24 padding-bottom-16',
          'text-align-left'
        )}
        style={{ borderRadius: 16 }}
      >
        <div className="padding-8">
          <div style={{ minHeight: 90 }}>
            <WebsiteText variant="smallTitle">{plan.title}</WebsiteText>
            <p className="font-size-6 margin-top-8">{plan.description}</p>
          </div>

          <div className="margin-top-24">
            <WebsiteText variant="title" htmlTag="div">
              {plan.price === null ? (
                <FormattedMessage
                  id="home.pricing.customLabel"
                  defaultMessage="Custom"
                />
              ) : (
                `$${yearlyPrice}`
              )}
            </WebsiteText>
            <div className="font-size-7 margin-top-8 shade-20">
              {plan.price === null ? (
                <FormattedMessage
                  id="pricing.enterprise.customPricing"
                  defaultMessage="Custom pricing"
                />
              ) : (
                <FormattedMessage
                  id="home.pricing.perUserMonth"
                  defaultMessage="per user/month, billed yearly"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function SectionHomePricingTeaser() {
  const intl = useIntl()
  const routes = useRoutes()
  const plans = getPricingPlans(intl)

  const content = {
    title: intl.formatMessage({
      id: 'home.pricing.title',
      defaultMessage: 'Simple pricing that scales with your company',
    }),
    description: intl.formatMessage({
      id: 'home.pricing.subtitle',
      defaultMessage: 'Add power when your team grows.',
    }),
    button: (
      <Button
        variant="secondary"
        size="default"
        href={routes.pricing()}
        withChevron
      >
        {intl.formatMessage({
          id: 'home.pricing.viewAll',
          defaultMessage: 'See full pricing',
        })}
      </Button>
    ),
  }

  return (
    <Section
      background="moon-dust-lighter"
      containerVerticalPadding={120}
      className="text-align-center"
    >
      <TextLayout
        type="centered"
        title={content.title}
        titleMaxWidth={500}
        button={content.button}
      />

      <div className="grid-12 grid-gap-16 margin-top-40">
        {plans.map((plan) => (
          <TeaserPlanItem key={plan.id} plan={plan} />
        ))}
      </div>
    </Section>
  )
}
